/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import { ThumbUp as ThumbUpIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { SearchBar } from 'components/SearchBar';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import { getAllEnvironmentsRequest } from 'modules/environments/actions';
import {
  EnvironmentFields,
  ListEnvironmentDataProps,
} from 'modules/environments/interfaces';
import { modalClose } from 'modules/modal/actions';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { EnvironmentFieldsProps } from '.';
import styles from './styles';

const useStyles = makeStyles(styles);

interface EnvironmentListProps {
  companySelected: CompanyData | null;
  environmentFields: EnvironmentFieldsProps | null;
  environmentList: ListEnvironmentDataProps;
  partnerSelected: PartnerData | null;
  isEdit?: boolean;
  isLoading: boolean;
  permissions?: PermissionsData;
  stateKey: string;
  getAllEnvironmentsRequest: (props) => void;
  modalClose: () => void;
  setEnvironmentFields: React.Dispatch<
    React.SetStateAction<EnvironmentFieldsProps>
  >;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}

const EnvironmentList = ({
  companySelected,
  environmentFields,
  environmentList,
  partnerSelected,
  isEdit,
  isLoading,
  permissions,
  stateKey,
  getAllEnvironmentsRequest,
  modalClose,
  setEnvironmentFields,
  setFieldValue,
}: EnvironmentListProps) => {
  const classes = useStyles();
  const {
    itemOrder,
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters();
  const canListEnvironmentFromAllPartners =
    permissions?.can_view_all_resources && !isEdit;

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      order: true,
      key: 'environment_name',
      i18n: 'ENVIRONMENT_NAME_KEY',
      style: { width: canListEnvironmentFromAllPartners ? '50%' : '100%' },
    },
    {
      order: true,
      key: 'partner',
      i18n: 'PARTNER_NAME_KEY',
      style: { width: '50%' },
      hidden: !canListEnvironmentFromAllPartners,
    },
  ].filter(item => !item?.hidden);

  const getEnvironments = () => {
    if (!partnerSelected && !canListEnvironmentFromAllPartners) return;

    let data = {
      ...query,
      company_uuid: companySelected?.id,
      partner_uuid: partnerSelected?.id,
    };

    if (canListEnvironmentFromAllPartners) {
      data = {
        ...data,
        company_uuid: '',
        partner_uuid: '',
      };
    }

    getAllEnvironmentsRequest(data);
  };

  React.useEffect(() => {
    getEnvironments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <Grid container>
      <SearchBar
        id="search-environments"
        isLoading={isLoading}
        handleSearch={handleSearch}
        setSearchValue={setSearchValue}
      />

      <TableItems
        header={
          <>
            <TableCell>
              <></>
            </TableCell>
            {headers.map(item => (
              <TableCell
                key={item?.i18n}
                order={item?.order}
                style={item?.style}
                markOderItem={itemOrder === item?.key}
                handleOrder={() =>
                  item?.order ? handleOrder(item?.key) : null
                }
              >
                {i18n.t(item?.i18n)}
              </TableCell>
            ))}
          </>
        }
        renderItems={(item: EnvironmentFields) => (
          <TableRow
            key={item?.environment_uuid}
            onClick={() => {
              setFieldValue(stateKey, item?.environment_uuid);
              setEnvironmentFields({
                environment_name: item?.name,
                environment_uuid: item?.environment_uuid,
              });
              modalClose();
            }}
            className={clsx(classes.tableRow, {
              [classes.environmentSelected]:
                environmentFields?.environment_uuid === item?.environment_uuid,
            })}
          >
            <TableCell className={classes.likeCell}>
              {environmentFields?.environment_uuid ===
              item?.environment_uuid ? (
                <ThumbUpIcon fontSize="small" />
              ) : (
                <></>
              )}
            </TableCell>
            <TableCell ellipsis>{item?.name}</TableCell>
            {canListEnvironmentFromAllPartners && (
              <TableCell ellipsis>{item?.partner?.name}</TableCell>
            )}
          </TableRow>
        )}
        data={environmentList}
        isLoading={isLoading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        overrideRowsCount={query.limit}
        notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_ENVIRONMENT_KEY')}
      />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected, partnerSelected },
  environments: { isLoading, environmentList },
}: ApplicationState) => ({
  companySelected,
  environmentList,
  partnerSelected,
  isLoading,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getAllEnvironmentsRequest,
  modalClose,
})(EnvironmentList);
